import type { ColorSpec } from './types';

/** Purple color spec — uses the `purple` Tailwind palette. */
export const purple: ColorSpec = {
  filled: {
    border: 'border-purple-700 dark:border-purple-500',
    bg: 'bg-purple-600 text-white dark:bg-purple-700',
    bgActive: 'bg-purple-800 text-white',
    hover: 'hover:bg-purple-700 hover:text-white active:bg-purple-800 active:text-white',
    hoverDark: 'dark:hover:bg-purple-800 dark:hover:text-white dark:active:bg-purple-900 dark:active:text-white',
  },
  outline: {
    border: 'border-purple-400 dark:border-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-200',
    bgActive: 'bg-purple-600 dark:bg-purple-500 text-white',
    hover: 'hover:bg-purple-100 hover:text-purple-800 active:bg-purple-200 active:text-purple-800',
    hoverDark:
      'dark:hover:bg-purple-900 dark:hover:text-purple-100 dark:active:bg-purple-800 dark:active:text-purple-100',
    hoverWhenActive: 'hover:bg-purple-700 dark:hover:bg-purple-600',
  },
  soft: {
    border: 'border-purple-100 dark:border-purple-900',
    bg: 'bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 font-normal',
    bgActive: 'bg-purple-600 dark:bg-purple-500 text-white',
    hover: 'hover:bg-purple-600 hover:text-white active:bg-purple-700 active:text-white',
    hoverDark: 'dark:hover:bg-purple-500 dark:hover:text-white dark:active:bg-purple-600 dark:active:text-white',
    hoverWhenActive: 'hover:bg-purple-700 dark:hover:bg-purple-600',
  },
  textFilled: {
    text: 'text-purple-600 dark:text-purple-400',
    activeBg: 'bg-purple-600 text-white dark:bg-purple-700',
    hover: 'hover:bg-purple-600 hover:text-white active:bg-purple-700',
    hoverDark: 'dark:hover:bg-purple-700 dark:hover:text-white dark:active:bg-purple-800',
  },
  textSoft: {
    text: 'text-purple-700 dark:text-purple-300 font-normal',
    activeBg: 'bg-purple-600 text-white dark:bg-purple-500',
    hover: 'hover:bg-purple-100 hover:text-purple-900 active:bg-purple-200',
    hoverDark: 'dark:hover:bg-purple-900 dark:hover:text-purple-100 dark:active:bg-purple-800',
  },
  tooltip: 'bg-purple-600 dark:bg-purple-700 text-white',
  badge: {
    filled: 'bg-purple-600 dark:bg-purple-700 text-white',
    soft: 'bg-purple-400 dark:bg-purple-500 text-white',
  },
  softIcon: 'text-purple-700 dark:text-purple-200 group-hover/btn:text-current',
  underline: {
    filled:
      '[&_u]:text-purple-200 dark:[&_u]:text-purple-300 [&_u]:decoration-purple-300 dark:[&_u]:decoration-purple-400',
    outline:
      '[&_u]:text-purple-800 dark:[&_u]:text-purple-300 [&_u]:decoration-purple-600 dark:[&_u]:decoration-purple-400',
    soft: '[&_u]:text-purple-800 dark:[&_u]:text-purple-300 [&_u]:decoration-purple-300 dark:[&_u]:decoration-purple-400',
  },
  input: {
    filled: {
      border: 'border-purple-700 dark:border-purple-500',
      bg: 'bg-purple-600 dark:bg-purple-700',
      focusBg: 'focus-within:bg-purple-700 dark:focus-within:bg-purple-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-purple-700 dark:focus-within:outline-purple-500',
      hoverActive:
        'hover:bg-purple-700 hover:text-white active:bg-purple-800 active:text-white dark:hover:bg-purple-800 dark:active:bg-purple-900',
      readOnlyBg: 'bg-purple-500 dark:bg-purple-800',
      text: 'text-white',
    },
    outline: {
      border: 'border-purple-400 dark:border-purple-500',
      bg: 'bg-purple-50 dark:bg-purple-950',
      focusBg: 'focus-within:bg-purple-100 dark:focus-within:bg-purple-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-purple-400 dark:focus-within:outline-purple-500',
      hoverActive:
        'hover:bg-purple-100 hover:text-purple-800 active:bg-purple-200 active:text-purple-800 dark:hover:bg-purple-900 dark:hover:text-purple-100 dark:active:bg-purple-800 dark:active:text-purple-100',
      readOnlyBg: 'bg-purple-50 dark:bg-purple-900',
      text: 'text-purple-700 dark:text-purple-200',
    },
    soft: {
      border: 'border-purple-100 dark:border-purple-900',
      bg: 'bg-purple-50 dark:bg-purple-950',
      focusBg: 'focus-within:bg-purple-100 dark:focus-within:bg-purple-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-purple-100 dark:focus-within:outline-purple-900',
      hoverActive:
        'hover:bg-purple-100 hover:text-purple-900 active:bg-purple-200 active:text-purple-950 dark:hover:bg-purple-900 dark:hover:text-purple-100 dark:active:bg-purple-800 dark:active:text-purple-100',
      readOnlyBg: 'bg-purple-50 dark:bg-purple-900',
      text: 'text-purple-700 dark:text-purple-300',
    },
  },
  inputMuted: {
    filled: 'text-white',
    outline: 'text-purple-300 dark:text-purple-600',
    soft: 'text-purple-300 dark:text-purple-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-white',
    outline: 'placeholder:text-purple-300 dark:placeholder:text-purple-600',
    soft: 'placeholder:text-purple-300 dark:placeholder:text-purple-600',
  },
  group: {
    filled: 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950',
    outline: 'border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-950',
    soft: 'border-purple-200 dark:border-purple-800 bg-purple-100 dark:bg-purple-900',
  },
  groupDivider: {
    filled: 'bg-purple-300 dark:bg-purple-700',
    outline: 'bg-purple-300 dark:bg-purple-600',
    soft: 'bg-purple-200 dark:bg-purple-700',
  },
  segment: {
    filled: 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950',
    outline: 'border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-950',
  },
  segmentDivider: {
    filled: 'bg-purple-300 dark:bg-purple-700',
    outline: 'bg-purple-300 dark:bg-purple-600',
  },
  popoverHighlight: 'bg-purple-600 text-white',
  popoverText: 'text-purple-700 dark:text-purple-200',
  popoverBorder: 'border-purple-200 dark:border-purple-700',
};
